import { ApplicationConfig, APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { provideRouter, TitleStrategy } from '@angular/router';
import { LocalizedTitleStrategy } from './localized-title.strategy';
import { provideHttpClient, withInterceptors, withFetch } from '@angular/common/http';
import { provideZoneChangeDetection } from '@angular/core';
import { registerLocaleData, PRECONNECT_CHECK_BLOCKLIST } from '@angular/common';
import localeEsPe from '@angular/common/locales/es-PE';
import localeEnUs from '@angular/common/locales/en';
import localePtBr from '@angular/common/locales/pt';
import { routes } from './app.routes';
import { catchError, EMPTY, tap } from 'rxjs';
import { AppConfig } from '@env/app-config';

// i18n: traducciones en runtime con Transloco
import { provideTransloco } from '@jsverse/transloco';
import { TranslocoHttpLoader } from './transloco-loader';
import { LanguageService, LANG_STORAGE_KEY } from '@shared/services/usuario/language.service';

// Zona horaria: resuelve y propaga la zona del usuario para el formateo de fechas
import { TimezoneService } from '@shared/services/usuario/timezone.service';
import { HEADER_EXTRA_ACTIONS } from '@shared/layout/header-extra-actions.token';
import { NotificationIconComponent } from '@partnercore/feature-modules/modulos/control-general/notificaciones/notification-dropdown/notification-icon.component';
import { SETTINGS_EXTRA_TABS } from '@partnercore/feature-settings/configuracion/settings-extra-tabs.token';
import { ConfigMonedasComponent } from '@partnercore/feature-settings/configuracion/components/config-monedas/config-monedas.component';
import { CURRENCY_PICKER_SOURCE } from '@shared/services/currency/currency-picker-source';
import { ClientCurrencyPickerSource } from '@shared/services/client-currency/client-currency-picker-source';

// Locale data para DatePipe/number/currency y el pipe appDate (es-PE por defecto; en/pt para i18n)
registerLocaleData(localeEsPe);
registerLocaleData(localeEnUs);
registerLocaleData(localePtBr);

/** Mapea el código de idioma de la app (ISO 639-1) al locale de formato de Angular. */
const LANG_TO_LOCALE: Record<string, string> = { es: 'es-PE', en: 'en-US', pt: 'pt-BR' };

/**
 * LOCALE_ID inicial para los pipes nativos de Angular (fecha/número/moneda).
 * Se resuelve al arranque desde el último idioma recordado en localStorage; el cambio
 * reactivo del formato sin recargar es una fase posterior del roadmap i18n.
 */
function resolveInitialLocaleId(): string {
  try {
    const lang = localStorage.getItem(LANG_STORAGE_KEY);
    if (lang && LANG_TO_LOCALE[lang]) {
      return LANG_TO_LOCALE[lang];
    }
  } catch {
    /* localStorage no disponible — usar default */
  }
  return 'es-PE';
}

// Interceptores modernos (funcionales)
import { authInterceptor } from '@interceptors/auth.interceptor';
import { errorInterceptor } from '@interceptors/error.interceptor';
import { moduleContextInterceptor } from '@interceptors/module-context.interceptor';
import { companyContextInterceptor } from '@interceptors/company-context.interceptor';
import { languageInterceptor } from '@interceptors/language.interceptor';

// AG Grid (locale, opciones globales) se configura en `@shared/components/ag-grid/ag-grid`,
// no aquí: importarlo desde el arranque metía toda la librería en el bundle inicial.

// Session recovery
import { AuthService } from '@shared/services/auth.service';
import { AuthApiService } from '@shared/services/auth-api.service';

/**
 * APP_INITIALIZER factory: antes de que Angular active cualquier ruta, intenta
 * recuperar el access token haciendo una llamada silenciosa a /auth/refresh-token.
 * El navegador envia automaticamente la cookie HttpOnly "rt" si existe.
 *
 * Esto permite que el usuario siga autenticado tras un refresco de pagina
 * sin almacenar tokens en localStorage.
 */
function initializeSession(authService: AuthService, authApi: AuthApiService): () => Promise<void> {
  return () => {
    if (!authService.hasStoredUser()) {
      return Promise.resolve();
    }

    return authApi.refreshToken().pipe(
      tap((res: any) => authService.recoverSession(res.user)),
      catchError(() => {
        authService.clearLocalSession();
        return EMPTY;
      })
    ).toPromise().then(() => {});
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    // Título de pestaña localizado (traduce el `title` de cada ruta como clave i18n).
    { provide: TitleStrategy, useClass: LocalizedTitleStrategy },
    // Campana de notificaciones: acción extra del header (ver header-extra-actions.token.ts).
    { provide: HEADER_EXTRA_ACTIONS, useValue: NotificationIconComponent, multi: true },
    // Pestaña "Monedas" de Configuración: ver settings-extra-tabs.token.ts.
    {
      provide: SETTINGS_EXTRA_TABS,
      useValue: {
        id: 'monedas',
        label: 'settings.nav.monedasLabel',
        description: 'settings.nav.monedasDesc',
        icon: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z',
        component: ConfigMonedasComponent,
        ownerOnly: false,
      },
      multi: true,
    },
    // Selector de moneda (empresas, perfil, configuración general): lista habilitada del cliente
    // en vez del catálogo completo por defecto. Ver currency-picker-source.ts.
    { provide: CURRENCY_PICKER_SOURCE, useClass: ClientCurrencyPickerSource },

    provideHttpClient(
      withFetch(),
      withInterceptors([languageInterceptor, authInterceptor, moduleContextInterceptor, companyContextInterceptor, errorInterceptor])
    ),

    // i18n: Transloco con idiomas es/en/pt y carga de traducciones por HTTP
    provideTransloco({
      config: {
        availableLangs: ['es', 'en', 'pt'],
        defaultLang: 'es',
        fallbackLang: 'es',
        reRenderOnLangChange: true,
        prodMode: AppConfig.isProduction,
        missingHandler: { useFallbackTranslation: true },
      },
      loader: TranslocoHttpLoader,
    }),

    {
      provide: APP_INITIALIZER,
      useFactory: initializeSession,
      deps: [AuthService, AuthApiService],
      multi: true,
    },

    // Locale de formato (fecha/número/moneda): se resuelve al arranque desde el idioma recordado
    { provide: LOCALE_ID, useFactory: resolveInitialLocaleId },

    // NgOptimizedImage: los logos con `priority` son assets servidos por el PROPIO origen, así que
    // un <link rel="preconnect"> no aportaría nada (la conexión ya está abierta al cargar el HTML).
    // Se exime al origen actual para no arrastrar el aviso NG02956 en cada arranque.
    {
      provide: PRECONNECT_CHECK_BLOCKLIST,
      useValue: typeof window !== 'undefined' ? [window.location.origin] : [],
      multi: true,
    },

    // Instancia TimezoneService al arranque para que empiece a sincronizar la zona del usuario
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [TimezoneService],
      multi: true,
    },

    // Preload del idioma recordado ANTES del primer render: deja el bundle de traducción en
    // memoria (evita "missing translation" en las traducciones síncronas tipo cabeceras AG-Grid)
    // y fija el locale de formato. Devuelve la promesa para que Angular espere a que resuelva.
    {
      provide: APP_INITIALIZER,
      useFactory: (lang: LanguageService) => () => lang.init(),
      deps: [LanguageService],
      multi: true,
    },
  ],
};